import { removeArrayItem, updateArrayItem } from '@/util/array';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '@/util/event-bus';
import i18n from '@/i18n/i18n-config';
import { mapErrorsToInputs } from '@/util/forms';
import userCodesService from '@/api/user-codes-service';

const getDefaultFormItem = () => ({});

export const initialUserCodesFilterParams = () => ({});

const state = {
  userCodes: null,
  userCodesPagination: {},
  newUserCode: getDefaultFormItem(),
  editedUserCode: {},
  userCodeValidationErrors: {},
  userCodesFilterParams: initialUserCodesFilterParams(),
};

const mutations = {
  SET_USER_CODES(state, { data, current_page, per_page, total }) {
    state.userCodes = data;
    state.userCodesPagination = {
      current_page,
      per_page,
      total,
    };
  },

  SET_FILTER_PARAMS(state, params) {
    state.userCodesFilterParams = params;
  },

  STORE_USER_CODE(state, userCode) {
    state.userCodes = [...state.userCodes, userCode];
    state.newUserCode = getDefaultFormItem();
    state.userCodeValidationErrors = {};
    state.userCodesPagination.total += 1;
  },

  SET_EDITED_USER_CODE(state, userCode) {
    state.userCodeValidationErrors = {};
    state.editedUserCode = JSON.parse(JSON.stringify(userCode));
  },

  UPDATE_USER_CODE(state, userCode) {
    state.userCodes = updateArrayItem(state.userCodes, userCode);
  },

  DELETE_USER_CODE(state, userCode) {
    state.userCodes = removeArrayItem(state.userCodes, userCode);
    state.userCodesPagination.total -= 1;
  },

  SET_USER_CODE_VALIDATION_ERRORS(state, validationErrors) {
    state.userCodeValidationErrors = validationErrors;
  },

  CLEAR_VALIDATION_ERRORS(state, field) {
    delete state.userCodeValidationErrors[field];
  },
};

const actions = {
  fetchUserCodes({ state, commit }, params) {
    commit('SET_FILTER_PARAMS', params);
    return userCodesService.getPage(params).then((res) => {
      commit('SET_USER_CODES', res.data);
      if (params.page !== state.userCodesFilterParams.page) {
        window.scrollTo(0, 0);
      }
    });
  },

  storeUserCode({ commit }, userCode) {
    return userCodesService
      .create(userCode)
      .then((res) => {
        commit('STORE_USER_CODE', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('user_code_created'));
        return res;
      })
      .catch((err) => {
        commit('SET_USER_CODE_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  editUserCode({ state, commit }, userCodeId) {
    const userCode = state.userCodes?.find((s) => s.id === userCodeId);
    if (userCode) {
      commit('SET_EDITED_USER_CODE', userCode);
      return Promise.resolve(userCode);
    }
    return userCodesService.getById(userCodeId).then((res) => {
      commit('SET_EDITED_USER_CODE', res.data);
      return res.data;
    });
  },

  updateUserCode({ commit }, userCode) {
    return userCodesService
      .update(userCode)
      .then((res) => {
        commit('UPDATE_USER_CODE', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('user_code_updated'));
      })
      .catch((err) => {
        commit('SET_USER_CODE_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  deleteUserCode({ commit }, userCode) {
    openConfirmDialog({
      title: i18n.t('confirm_user_code_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      userCodesService.delete(userCode).then(() => {
        commit('DELETE_USER_CODE', userCode);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('user_code_deleted'));
      });
    });
  },

  clearUserCodeValidationErrors({ commit }, field) {
    commit('CLEAR_VALIDATION_ERRORS', field);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
